import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { HMI_STATE, STATE_IDS, pyFloat } from './screenData';
import { Intro, Reveal, Segmented } from './ui';
import { StudyText } from './studies';
import styles from './ScreenInspector.module.css';

// The Overview tree from the library README, on the mock panel.
const NODES = [
    { key: 'page', depth: 0, fn: 'choose', value: () => 'Overview' },
    { key: 'status', depth: 1, fn: 'choose', value: (s) => s.status },
    { key: 'pressure', depth: 1, fn: 'read_number', value: (s) => Number(s.pressure) },
    { key: 'loading', depth: 1, fn: null },
    { key: 'mode', depth: 2, fn: 'choose', value: (s) => s.mode },
    { key: 'full_load', depth: 2, fn: 'check', value: (s) => s.load === 'full' },
    { key: 'no_alarms', depth: 1, fn: 'check', value: (s) => !s.alarm },
    { key: 'first_alarm', depth: 2, fn: 'read', branch: true, value: (s) => s.alarm },
];
const ASK_MS = 620;
const QUICK_MS = 200;

function isSkipped(node, s) {
    return node.branch && !s.alarm;
}

// JSON with Python-style floats (38.0), like NodeResult.to_json().
function toJson(v, ind = '') {
    if (v === null || v === undefined) return 'null';
    if (typeof v === 'number') return pyFloat(v);
    if (typeof v === 'boolean') return String(v);
    if (typeof v === 'string') return JSON.stringify(v);
    const keys = Object.keys(v);
    if (!keys.length) return '{}';
    const inner = `${ind}  `;
    return `{\n${keys.map((k) => `${inner}${JSON.stringify(k)}: ${toJson(v[k], inner)}`).join(',\n')}\n${ind}}`;
}

function buildResult(s, done) {
    const page = {};
    if (done.has('page')) page.value = 'Overview';
    if (done.has('status')) page.status = s.status;
    if (done.has('pressure')) page.pressure = Number(s.pressure);
    if (done.has('loading')) {
        const loading = {};
        if (done.has('mode')) loading.mode = s.mode;
        if (done.has('full_load')) loading.full_load = s.load === 'full';
        page.loading = loading;
    }
    if (done.has('no_alarms')) {
        page.no_alarms = done.has('first_alarm') ? { value: false, first_alarm: s.alarm } : !s.alarm;
    }
    return { page };
}

function JsonView({ text, fresh }) {
    return (
        <pre className={styles.json}>
            {text.split('\n').map((line, i) => {
                const m = line.match(/^(\s*)("[^"]+")(: )(.*?)(,?)$/);
                const isNew = fresh && line.includes(`"${fresh}"`);
                if (!m) return <div key={i}>{line}</div>;
                const [, sp, key, colon, val, comma] = m;
                let cls = styles.jsonNum;
                if (val.startsWith('"')) cls = styles.jsonStr;
                else if (val === 'true' || val === 'false') cls = styles.jsonBool;
                else if (val === '{') cls = '';
                return (
                    <motion.div
                        key={`${i}-${line}`}
                        className={isNew ? styles.jsonNew : undefined}
                        initial={isNew ? { opacity: 0, x: -6 } : false}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        {sp}<span className={styles.jsonKey}>{key}</span>{colon}<span className={cls}>{val}</span>{comma}
                    </motion.div>
                );
            })}
        </pre>
    );
}

export default function PageTree({ t }) {
    const tr = t.tree;
    const [state, setState] = useState('fault');
    const [cursor, setCursor] = useState(-1);
    const [running, setRunning] = useState(false);
    const s = HMI_STATE[state];

    const start = () => {
        setCursor(0);
        setRunning(true);
    };

    // Start asking as soon as the slide appears.
    useEffect(() => {
        const id = setTimeout(() => {
            setCursor(0);
            setRunning(true);
        }, 600);
        return () => clearTimeout(id);
    }, []);

    useEffect(() => {
        if (!running) return undefined;
        if (cursor >= NODES.length) {
            setRunning(false);
            return undefined;
        }
        const node = NODES[cursor];
        const ms = node.fn && !isSkipped(node, s) ? ASK_MS : QUICK_MS;
        const id = setTimeout(() => setCursor((c) => c + 1), ms);
        return () => clearTimeout(id);
    }, [running, cursor, s]);

    const pickState = (v) => {
        setState(v);
        start();
    };

    const asked = NODES.slice(0, Math.max(0, cursor)).filter((n) => !isSkipped(n, s));
    const done = new Set(asked.map((n) => n.key));
    const last = cursor > 0 ? NODES[cursor - 1] : null;
    const fresh = last && !isSkipped(last, s) ? last.key : null;

    return (
        <>
            <Intro eyebrow={tr.eyebrow} title={tr.title} sub={tr.sub} />

            <div className={styles.libControls}>
                <Segmented
                    small
                    group="state-tree"
                    label={t.stateLabel}
                    options={STATE_IDS.map((id) => ({ id, label: t.states[id] }))}
                    value={state}
                    onChange={pickState}
                />
                <button type="button" className={styles.textBtn} onClick={start} disabled={running}>
                    <ReplayRoundedIcon sx={{ fontSize: 17 }} />
                    {running ? tr.running : tr.run}
                </button>
            </div>

            <Reveal className={styles.treeGrid} delay={0.1}>
                <ul className={styles.tree}>
                    {NODES.map((n, i) => {
                        const skipped = isSkipped(n, s);
                        let st = 'idle';
                        if (i < cursor) st = skipped ? 'skip' : 'done';
                        else if (i === cursor && running && n.fn && !skipped) st = 'asking';
                        const cls = { asking: styles.nodeAsking, done: styles.nodeDone, skip: styles.nodeSkip, idle: '' }[st];
                        return (
                            <li key={n.key} className={`${styles.node} ${cls}`} style={{ marginLeft: n.depth * 22 }}>
                                <div className={styles.nodeTop}>
                                    <code className={styles.nodeName}>{n.key}</code>
                                    <span className={styles.nodeFn}>{n.fn || tr.group}</span>
                                    {n.branch && <span className={styles.nodeBranch}>{tr.ifFalse}</span>}
                                    <span className={styles.nodeValue}>
                                        {st === 'done' && n.fn && toJson(n.value(s))}
                                        {st === 'skip' && tr.skipped}
                                        {st === 'asking' && <span className={styles.dots}><i /><i /><i /></span>}
                                    </span>
                                </div>
                                {n.fn && <div className={styles.nodeQ}>{tr.questions[n.key]}</div>}
                            </li>
                        );
                    })}
                </ul>
                <JsonView text={toJson(buildResult(s, done))} fresh={fresh} />
            </Reveal>

            <p className={styles.note}><StudyText text={tr.note} /></p>
        </>
    );
}
